<script setup lang="ts">
import type { NavigationItem } from '~/models/navigation/NavigationItems.model'
import type { FooterLink } from '~/models/layouts/footer.model'
import { onMounted, onUnmounted, ref } from '#imports'

const props = withDefaults(
  defineProps<{
    isCollapsed: boolean
    userName: string
    userMail?: string
    userImage: string
    companyName?: string
    navigationItems: NavigationItem[]
    userMenuItems: NavigationItem[]
    footerLinks: FooterLink[]
  }>(),
  {
    userMail: undefined,
    companyName: undefined,
  }
)

const emit = defineEmits(['update:isCollapsed'])

const updateIsCollapsed = (value: boolean) => {
  emit('update:isCollapsed', value)
}

const navDrawerRef = ref<{ activateAndFocusFirstItem: () => void } | null>(null)

const handleFirstTab = (event: KeyboardEvent) => {
  if (event.key === 'Tab' && !event.shiftKey) {
    if (document.activeElement === document.body) {
      event.preventDefault()
      navDrawerRef.value?.activateAndFocusFirstItem()
      document.removeEventListener('keydown', handleFirstTab)
    }
  }
}

onMounted(() => {
  document.addEventListener('keydown', handleFirstTab)
})

onUnmounted(() => {
  document.removeEventListener('keydown', handleFirstTab)
})
</script>

<template>
  <AppNavigationDrawer
    ref="navDrawerRef"
    :is-collapsed="props.isCollapsed"
    position="left"
    :user-name="props.userName"
    :user-mail="props.userMail"
    :user-image="props.userImage"
    :company-name="props.companyName"
    :navigation-items="props.navigationItems"
    :footer-links="props.footerLinks"
    :user-menu-items="props.userMenuItems"
    aria-label="Main navigation"
    data-testid="app-navigation-drawer"
    @update:is-collapsed="updateIsCollapsed"
  >
    <div
      class="bg-gray-50 flex min-h-screen flex-col"
      role="main"
      data-testid="main-content"
    >
      <div class="flex-grow">
        <div
          class="container mx-auto px-10 py-10"
          role="region"
          aria-label="Page content"
        >
          <slot />
        </div>
      </div>
      <AppFooter :footer-links="footerLinks" />
    </div>
  </AppNavigationDrawer>
</template>
